import {
  Box,
  Flex,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { NewsConfig } from "../homeBlockConfig";
import classes from "../homeBlock.module.css";
import {
  ArticleCategorySelect,
  ArticleOption,
  PickerRow,
  searchArticles,
  SearchPicker,
} from "../RemotePickers";
import {
  applyAction,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconLabelList,
  iconLabelItem,
  IconPicker,
  ImagePicker,
  lucideIcon,
  newId,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.news;

// Tên chuyên mục mẫu theo thiết kế; khi tải xong Danh mục bài viết thì tự khớp theo tên để lấy id
const SAMPLE_CATEGORIES = [
  "Review Sản Phẩm",
  "Tư Vấn Mua Hàng",
  "Kiến Thức & Thủ Thuật",
  "Hướng Dẫn Build PC",
  "Sự Kiện & Cộng Đồng",
  "Khuyến Mãi",
];

const SIDE_SORT_OPTIONS: { value: NewsConfig["articles"]["side"]["sort"]; label: string }[] = [
  { value: "views_7d", label: "Lượt xem 7 ngày" },
  { value: "views_30d", label: "Lượt xem 30 ngày" },
  { value: "newest", label: "Mới nhất" },
];

const promise = (icon: string, title: string, description: string) => ({
  id: newId(),
  icon: lucideIcon(icon),
  title,
  description,
});

const initialConfig = (block: HomeBlock): NewsConfig => {
  const sample = block.code === "home-news";
  return {
    heading: {
      eyebrow: sample ? "TIN TỨC - KIẾN THỨC CÔNG NGHỆ" : "",
      title: sample ? "CẬP NHẬT CÔNG NGHỆ" : block.name,
      highlight: sample ? "MỖI NGÀY" : "",
      description: sample
        ? "Tin tức mới nhất - Đánh giá chuyên sâu - Hướng dẫn chi tiết - Xu hướng công nghệ"
        : "",
      allLabel: sample ? "Xem tất cả bài viết" : "",
      allLink: sample ? "/tin-tuc" : "",
    },
    tabs: sample
      ? SAMPLE_CATEGORIES.map((name) => ({ id: newId(), name, category: { id: null, name } }))
      : [],
    articles: {
      featuredMode: "latest",
      featured: { id: null, title: "" },
      gridCount: 6,
      side: { title: sample ? "BÀI VIẾT NỔI BẬT" : "", sort: "views_7d", count: 5 },
    },
    banner: {
      image: null,
      title: sample ? "KIẾN THỨC CÔNG NGHỆ NÂNG TẦM TRẢI NGHIỆM" : "",
      bullets: sample
        ? [
            iconLabelItem("file-text", "Bài viết chuyên sâu từ chuyên gia"),
            iconLabelItem("sparkles", "Cập nhật xu hướng mới nhất"),
            iconLabelItem("user-check", "Giúp bạn lựa chọn dễ dàng hơn"),
          ]
        : [],
      buttonLabel: sample ? "Khám phá ngay" : "",
      link: sample ? "/kien-thuc" : "",
    },
    promises: sample
      ? [
          promise("file-text", "Nội dung chuyên sâu", "Được biên tập bởi chuyên gia"),
          promise("shield-check", "Thông tin chính xác", "Cập nhật liên tục"),
          promise("users", "Cộng đồng công nghệ", "Chia sẻ và thảo luận"),
          promise("star", "Ứng dụng thực tế", "Dễ hiểu - Dễ áp dụng"),
        ]
      : [],
    footerButton: {
      label: sample ? "Khám phá thêm bài viết" : "",
      link: sample ? "/tin-tuc" : "",
    },
  };
};

const articleRow = (a: ArticleOption) => <PickerRow image={a.image} title={a.title} sub={a.date} />;

const EditNews = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading, articles, banner } = config;
  // Ảnh banner mẫu cắt từ thiết kế: chỉ để xem, gỡ thì ẩn; không nằm trong config
  const [showSampleBanner, setShowSampleBanner] = useState(block.code === "home-news");

  const patchHeading = (v: Partial<NewsConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const patchArticles = (v: Partial<NewsConfig["articles"]>) =>
    setConfig((c) => ({ ...c, articles: { ...c.articles, ...v } }));
  const patchSide = (v: Partial<NewsConfig["articles"]["side"]>) =>
    setConfig((c) => ({ ...c, articles: { ...c.articles, side: { ...c.articles.side, ...v } } }));
  const patchBanner = (v: Partial<NewsConfig["banner"]>) =>
    setConfig((c) => ({ ...c, banner: { ...c.banner, ...v } }));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn nhỏ"
            value={heading.eyebrow}
            onChange={(e) => patchHeading({ eyebrow: e.currentTarget.value })}
          />
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Mô tả"
            value={heading.description}
            onChange={(e) => patchHeading({ description: e.currentTarget.value })}
          />
          <TextInput
            label="Nhãn Xem tất cả"
            value={heading.allLabel}
            onChange={(e) => patchHeading({ allLabel: e.currentTarget.value })}
          />
          <TextInput
            label="Link"
            value={heading.allLink}
            onChange={(e) => patchHeading({ allLink: e.currentTarget.value })}
          />
        </SimpleGrid>
      </SectionCard>

      <SectionCard
        title="Tab chuyên mục"
        right={
          <Text fz={13} className={classes.muted}>
            Chọn từ Danh mục bài viết, lưu theo id
          </Text>
        }
      >
        <EditableList
          items={config.tabs}
          onChange={fieldSetter(setConfig, "tabs")}
          itemLabel={(t) => t.name}
          columns={[
            { label: "Tên tab", width: "minmax(0, 1fr)" },
            { label: "Chuyên mục bài viết", width: "minmax(0, 1fr)" },
          ]}
          renderCells={(t, update) => (
            <>
              <TextInput
                aria-label="Tên tab"
                placeholder={t.category.name || "Tên chuyên mục"}
                value={t.name}
                onChange={(e) => update({ name: e.currentTarget.value })}
              />
              <ArticleCategorySelect
                ariaLabel={`Chuyên mục của tab ${t.name}`}
                value={t.category}
                onChange={(category) => update({ category })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), name: "", category: { id: null, name: "" } })}
          addLabel="Thêm tab"
          max={8}
          minWidth={480}
        />
      </SectionCard>

      <SectionCard title="Bài viết">
        <Box>
          <Text fz={14} fw={500} mb={6} id="hb-news-featured">
            Bài lớn bên trái
          </Text>
          <SegmentedControl
            aria-labelledby="hb-news-featured"
            value={articles.featuredMode}
            onChange={(value) =>
              patchArticles({ featuredMode: value as NewsConfig["articles"]["featuredMode"] })
            }
            data={[
              { value: "latest", label: "Mới nhất của tab" },
              { value: "manual", label: "Chọn tay" },
            ]}
          />
        </Box>
        {articles.featuredMode === "manual" && (
          <Group gap={12} wrap="nowrap" className={`${classes.pickedRow} ${classes.pickedList}`}>
            <Box miw={0} style={{ flex: "1 1 auto" }}>
              <Text fz={13} truncate c={articles.featured.id === null ? "dimmed" : undefined}>
                {articles.featured.title || "Chưa chọn bài viết"}
              </Text>
              {articles.featured.id !== null && (
                <Text fz={12} className={`${classes.mono} ${classes.muted}`}>
                  Bài viết #{articles.featured.id}
                </Text>
              )}
            </Box>
            <SearchPicker
              buttonLabel={articles.featured.id === null ? "Chọn bài viết" : "Đổi bài"}
              searchPlaceholder="Tìm bài viết theo tiêu đề"
              search={searchArticles}
              itemKey={(a) => String(a.id)}
              renderItem={articleRow}
              onPick={(a) => patchArticles({ featured: { id: a.id, title: a.title } })}
            />
          </Group>
        )}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <NumberInput
            label="Số bài ở lưới giữa"
            min={2}
            max={8}
            value={articles.gridCount}
            onChange={(value) => typeof value === "number" && patchArticles({ gridCount: value })}
          />
          <TextInput
            label="Tiêu đề cột phải"
            value={articles.side.title}
            onChange={(e) => patchSide({ title: e.currentTarget.value })}
          />
          <Select
            label="Cột phải xếp theo"
            data={SIDE_SORT_OPTIONS}
            value={articles.side.sort}
            onChange={(value) =>
              value && patchSide({ sort: value as NewsConfig["articles"]["side"]["sort"] })
            }
            allowDeselect={false}
          />
          <NumberInput
            label="Số bài cột phải"
            min={3}
            max={8}
            value={articles.side.count}
            onChange={(value) => typeof value === "number" && patchSide({ count: value })}
          />
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Banner bên phải đầu block">
        <Flex wrap="wrap" gap={16} align="flex-start">
          <Box style={{ flex: "0 1 360px", minWidth: 220 }}>
            <ImagePicker
              label="Ảnh"
              src={banner.image}
              imageId={showSampleBanner ? IMG : undefined}
              crop={{ x: 662, y: 12, w: 592, h: 112 }}
              onPick={(url) => patchBanner({ image: url })}
              onClear={() => {
                setShowSampleBanner(false);
                patchBanner({ image: null });
              }}
              height={110}
              maxWidth={360}
            />
          </Box>
          <SimpleGrid
            cols={{ base: 1, md: 2 }}
            spacing={12}
            style={{ flex: "1 1 360px", minWidth: 0 }}
          >
            <TextInput
              label="Tiêu đề"
              value={banner.title}
              onChange={(e) => patchBanner({ title: e.currentTarget.value })}
            />
            <TextInput
              label="Nhãn nút"
              value={banner.buttonLabel}
              onChange={(e) => patchBanner({ buttonLabel: e.currentTarget.value })}
            />
            <TextInput
              label="Link"
              value={banner.link}
              onChange={(e) => patchBanner({ link: e.currentTarget.value })}
            />
          </SimpleGrid>
        </Flex>
        <IconLabelList
          label="Gạch đầu dòng"
          items={banner.bullets}
          onChange={(action) =>
            setConfig((c) => ({
              ...c,
              banner: { ...c.banner, bullets: applyAction(action, c.banner.bullets) },
            }))
          }
          max={3}
          addLabel="Thêm dòng"
        />
      </SectionCard>

      <SectionCard title="Dải cam kết cuối block">
        <EditableList
          items={config.promises}
          onChange={fieldSetter(setConfig, "promises")}
          itemLabel={(p) => p.title}
          columns={[
            { label: "Icon", width: "170px" },
            { label: "Tiêu đề", width: "minmax(0, 1fr)" },
            { label: "Mô tả", width: "minmax(0, 1fr)" },
          ]}
          renderCells={(p, update) => (
            <>
              <IconPicker
                ariaLabel={`Icon ${p.title}`}
                value={p.icon}
                onChange={(value) => update({ icon: value })}
              />
              <TextInput
                aria-label="Tiêu đề cam kết"
                value={p.title}
                onChange={(e) => update({ title: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Mô tả ${p.title}`}
                value={p.description}
                onChange={(e) => update({ description: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), icon: null, title: "", description: "" })}
          addLabel="Thêm cam kết"
          max={4}
          minWidth={560}
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nút cuối block"
            value={config.footerButton.label}
            onChange={(e) => {
              const label = e.currentTarget.value;
              setConfig((c) => ({ ...c, footerButton: { ...c.footerButton, label } }));
            }}
          />
          <TextInput
            label="Link"
            value={config.footerButton.link}
            onChange={(e) => {
              const link = e.currentTarget.value;
              setConfig((c) => ({ ...c, footerButton: { ...c.footerButton, link } }));
            }}
          />
        </SimpleGrid>
      </SectionCard>
    </EditorLayout>
  );
};

export default EditNews;
